import { useCallback, useMemo, type ReactNode } from 'react';
import { MAX_ASSET_DURATION } from '@shared/constants';
import { applyPredefinedFreeVoiceFallback, useSessionStore } from '@/stores/session';
import { sweepClampOffsets } from '@/lib/timelineSweep';
import { AssetLengthControl } from '@/components/VideoPlayer/AssetLengthControl';
import styles from './FreeAssetHeader.module.css';
import { scriptAttrs } from '@/lib/scriptDetect';

interface FreeAssetHeaderProps {
  /** Rendered directly under the title row, before voice + length sections.
   *  The page-level VersionsBar mounts here for custom assets so it sits
   *  in the same vertical position it does for predefined assets (under
   *  the h1 / title input). FB4#? — 2026-04-28. */
  versionsBarSlot?: ReactNode;
}

/** Header strip for free-choice / custom assets — shows a static title
 *  (custom assets only — title is captured once at creation by
 *  CreateAssetModal and immutable after), a labelled voice picker dropdown
 *  gated by `config.customVoiceIds`, and (custom only) an Asset Length
 *  control just below the voice section. Mirrors vanilla behavior at
 *  legacy/index.html lines 4011-4025 (render) and 4203-4254 (handlers).
 *
 *  Without this header a custom asset has no way to acquire a voiceId, and
 *  TTSView's `disabledReason` permanently reads "Select a voice first" so
 *  generation is locked.
 *
 *  Layout (FB2#11/12/13 — 2026-04-28; title locked 2026-04-28):
 *    [ Static title (large)                          VoiceName ]
 *    [ Select a voice: [v]                                    ]
 *    [ Asset length:   [ ___s ]                               ]
 *
 *  The Asset Length input is the shared `AssetLengthControl` running in
 *  controlled mode — same input UX as predefined assets get above the
 *  Timeline, including freely-typeable `.5`/`20.5`-style values. */
export function FreeAssetHeader({ versionsBarSlot }: FreeAssetHeaderProps = {}) {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const config = useSessionStore((s) => s.config);
  const customAssets = useSessionStore((s) => s.customAssets);
  const upsertCustomAsset = useSessionStore((s) => s.upsertCustomAsset);
  const customDurations = useSessionStore((s) => s.customDurations);
  const setCustomDuration = useSessionStore((s) => s.setCustomDuration);
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);

  const predefinedCount = config?.assets?.length ?? 0;
  const customIdx = currentAssetIdx - predefinedCount;
  const isCustom = customIdx >= 0;
  const customAsset = isCustom ? customAssets[customIdx] : null;

  // For predefined free-choice assets, fall through to config.assets[idx],
  // applying vanilla's voice fallback (legacy line 4014). With the fallback,
  // `asset.voiceId` for predefined free assets is auto-populated from
  // config.voices[0] when the admin left it empty — `showVoicePicker` below
  // (gated on `!asset.voiceId`) then collapses to false, matching vanilla
  // which never renders a picker for predefined free assets.
  const predefined = !isCustom
    ? applyPredefinedFreeVoiceFallback(config?.assets, config?.voices)[currentAssetIdx]
    : null;
  const asset = customAsset ?? predefined ?? null;

  const customVoices = useMemo(() => {
    if (!config) return [];
    const ids = config.customVoiceIds ?? [];
    return ids
      .map((id) => config.voices.find((v) => v.id === id))
      .filter((v): v is NonNullable<typeof v> => !!v);
  }, [config]);

  // Voice names to render next to the static title (mirrors the muted
  // "VoiceName" suffix the h1 shows for predefined assets). Multi-voice
  // (#173): joins all selected voice display names with ", " so the talent
  // sees at a glance which voices the asset will generate against.
  const activeVoiceIds = useMemo(() => asset?.voiceIds ?? [], [asset?.voiceIds]);
  const activeVoiceNames = useMemo(() => {
    if (!config || activeVoiceIds.length === 0) return [];
    return activeVoiceIds
      .map((id) => config.voices.find((v) => v.id === id)?.name)
      .filter((n): n is string => !!n);
  }, [activeVoiceIds, config]);
  const activeVoiceLabel = activeVoiceNames.join(', ');

  const onVoiceToggle = useCallback((voiceId: string, checked: boolean) => {
    if (!customAsset) return;
    const cur = customAsset.voiceIds ?? [];
    const next = checked
      ? cur.includes(voiceId) ? cur : [...cur, voiceId]  // append (Position = Check-Order)
      : cur.filter((id) => id !== voiceId);
    upsertCustomAsset({ ...customAsset, voiceIds: next });
  }, [customAsset, upsertCustomAsset]);

  // Target duration — custom-asset only. Empty / 0 / NaN clears it (writes 0,
  // which `getAssetTimelineDuration` treats as "no target", falling through
  // to bed/default). Clamped to (0, MAX_ASSET_DURATION] with 0.1 resolution
  // — matches AssetLengthControl's quantization. On shrink we sweep-clamp
  // existing chips so favorited takes past the new boundary get pulled in.
  // The controlled-mode `AssetLengthControl` already handles parse + clamp +
  // 0.1 quantization; this handler just persists + sweeps.
  const durationValue = customDurations[currentAssetIdx] ?? 0;
  const onDurationChange = useCallback(
    (secs: number) => {
      // `secs` is already clamped to [0, MAX] and rounded to 0.1 by
      // AssetLengthControl. 0 means "clear / fall through".
      const clean = secs > 0 ? Math.min(secs, MAX_ASSET_DURATION) : 0;
      setCustomDuration(currentAssetIdx, clean);
      if (clean > 0) {
        // Per-track sweep (#173): cross-voice chips keep their overlaps.
        const voiceIds = asset?.voiceIds ?? [];
        updateActiveBlocks((prev) => sweepClampOffsets(prev, clean, voiceIds));
      }
    },
    [currentAssetIdx, setCustomDuration, updateActiveBlocks, asset?.voiceIds],
  );

  if (!asset) return null;
  // Voice picker is custom-asset only — admin save enforces min-1-voice
  // per predefined asset (ToolEdit.tsx min-1-voice gate, #173), so
  // predefined free-choice assets always ship with voiceIds set and never
  // need a talent-side picker. Legacy KV-records without voiceIds get
  // patched at the data layer by applyPredefinedFreeVoiceFallback above.
  const showVoicePicker = isCustom;
  // Title is collected once via CreateAssetModal and rendered as static
  // text here. Legacy custom assets (or any pre-modal data) without a
  // title fall back to "Untitled" — no rename UI.
  const showTitle = isCustom;
  // Asset Length sits below voice — only meaningful for custom assets
  // (predefined free assets get their length from admin or the timeline-
  // header AssetLengthControl).
  const showLengthInput = isCustom;

  // If there's nothing to show, render nothing (clean header for predefined
  // free assets with a voice already set).
  if (!showVoicePicker && !showTitle && !showLengthInput) return null;

  return (
    <div className={styles.row}>
      {showTitle && (
        <div className={styles.titleBlock}>
          <div className={styles.titleRow}>
            <h2 className={styles.titleStatic} {...scriptAttrs(asset.title)}>
              {asset.title?.trim() || 'Untitled'}
            </h2>
            {activeVoiceLabel && (
              <span className={styles.titleVoice}>{activeVoiceLabel}</span>
            )}
          </div>
        </div>
      )}
      {versionsBarSlot}
      {showVoicePicker && (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Select voices{' '}
            <span className={styles.voiceCountHint}>
              ({activeVoiceIds.length} / 3)
            </span>
            :
          </span>
          {customVoices.length === 0 ? (
            <span className={styles.voiceEmpty}>— No voices available —</span>
          ) : (
            <div className={styles.voiceChecks} role="group" aria-label="Voices">
              {customVoices.map((v) => {
                const checked = activeVoiceIds.includes(v.id);
                const atCap = !checked && activeVoiceIds.length >= 3;
                return (
                  <label
                    key={v.id}
                    className={styles.voiceCheck}
                    data-checked={checked || undefined}
                    data-disabled={atCap || undefined}
                    title={atCap ? 'Max. 3 Voices pro Asset' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={atCap}
                      onChange={(e) => onVoiceToggle(v.id, e.target.checked)}
                    />
                    <span>{v.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
      {showLengthInput && (
        <div className={styles.lengthRow}>
          <AssetLengthControl
            value={durationValue}
            onChange={onDurationChange}
            forceVisible
          />
        </div>
      )}
    </div>
  );
}
